import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "../components/ui/sheet";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../components/ui/tooltip";
import {
    BarChart3,
    LineChart as LineChartIcon,
    Activity,
    AlertTriangle,
    Camera,
    Map as MapIcon,
    HelpCircle,
    ExternalLink,
    TrendingDown,
    Star,
    ShieldCheck,
    AlertCircle,
    Waves,
    Coins,
    Flame,
} from "lucide-react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip as ChartTooltip,
    CartesianGrid,
    LineChart,
    Line,
    ComposedChart,
    Legend,
    Cell,
} from "recharts";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import api from "../lib/api";
import { formatEur, formatNumber, formatPct } from "../lib/format";
import { toast } from "sonner";

const DISCOUNT_BUCKETS = [
    { key: "20%+ over market", min: -Infinity, max: -20, color: "#ef4444" },
    { key: "10–20% over", min: -20, max: -10, color: "#f97316" },
    { key: "0–10% over", min: -10, max: 0, color: "#f59e0b" },
    { key: "0–5% under", min: 0, max: 5, color: "#a3a3a3" },
    { key: "5–10% under", min: 5, max: 10, color: "#84cc16" },
    { key: "10–20% under", min: 10, max: 20, color: "#22c55e" },
    { key: "20%+ under", min: 20, max: Infinity, color: "#10b981" },
];

const ZONE_FILL = {
    preferred: "#10b981",
    speculative: "#f59e0b",
    avoid: "#ef4444",
    unknown: "#a3a3a3",
};

const Market = () => {
    const navigate = useNavigate();
    const [overview, setOverview] = useState(null);
    const [areas, setAreas] = useState([]);
    const [history, setHistory] = useState(null);
    const [allListings, setAllListings] = useState([]);
    const [rankings, setRankings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [snapshotting, setSnapshotting] = useState(false);
    const [drawerArea, setDrawerArea] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [ov, ar, hi, ls, rk] = await Promise.all([
                api.get("/market/overview"),
                api.get("/market/areas"),
                api.get("/market/history?days=90"),
                api.get("/listings?limit=2000"),
                api.get("/market/rankings?limit=10"),
            ]);
            setOverview(ov.data);
            setAreas(ar.data);
            setHistory(hi.data);
            setAllListings(ls.data);
            setRankings(rk.data);
        } catch (e) {
            toast.error("Could not load market data", {
                description: e?.response?.data?.detail || String(e),
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    const captureSnapshot = async () => {
        setSnapshotting(true);
        try {
            const { data } = await api.post("/market/snapshot/run");
            toast.success("Snapshot captured", {
                description: `${data.captured_day} · ${data.areas_captured} areas`,
            });
            await reload();
        } catch (e) {
            toast.error("Snapshot failed", { description: String(e) });
        } finally {
            setSnapshotting(false);
        }
    };

    // ── derived chart data ──────────────────────────────────────────────
    const areaComparison = useMemo(() => {
        return [...areas]
            .filter((a) => a.median_price_per_sqm)
            .sort((a, b) => (b.active_listings || 0) - (a.active_listings || 0))
            .slice(0, 8)
            .map((a) => ({
                area: a.area,
                median: a.median_price_per_sqm,
                p25: a.p25_price_per_sqm,
                p75: a.p75_price_per_sqm,
                trimmedMean: a.trimmed_mean_price_per_sqm,
                listings: a.active_listings,
                outliers: a.excluded_outliers,
            }));
    }, [areas]);

    const discountHistogram = useMemo(() => {
        const counts = new Map(DISCOUNT_BUCKETS.map((b) => [b.key, 0]));
        const medians = {};
        for (const a of areas) {
            if (a.median_price_per_sqm) medians[a.area] = a.median_price_per_sqm;
        }
        let total = 0;
        for (const l of allListings) {
            if (l.excluded_from_market_stats) continue;
            const m = medians[l.municipality];
            const ppm = l.price_per_sqm_eur;
            if (!m || !ppm) continue;
            const pct = ((m - ppm) / m) * 100;
            const bucket = DISCOUNT_BUCKETS.find((b) => pct >= b.min && pct < b.max);
            if (bucket) {
                counts.set(bucket.key, counts.get(bucket.key) + 1);
                total++;
            }
        }
        return DISCOUNT_BUCKETS.map((b) => ({
            bucket: b.key,
            count: counts.get(b.key),
            color: b.color,
            pct: total > 0 ? (counts.get(b.key) / total) * 100 : 0,
        }));
    }, [areas, allListings]);

    const supplyTrend = useMemo(() => {
        if (!history?.available) return [];
        const byDay = {};
        for (const s of history.snapshots) {
            const d = s.captured_day;
            if (!byDay[d]) byDay[d] = { day: d, active: 0, outliers: 0, reductions: 0 };
            byDay[d].active += s.active_listings || 0;
            byDay[d].outliers += s.excluded_outliers || 0;
            byDay[d].reductions += s.price_reductions || 0;
        }
        return Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day));
    }, [history]);

    const geocodedListings = useMemo(() => {
        return allListings.filter((l) => l.latitude && l.longitude);
    }, [allListings]);

    return (
        <TooltipProvider delayDuration={150}>
            <div className="space-y-8" data-testid="market-page">
                <header className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-serif">Market Intelligence</h1>
                        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                            Real listings only. Statistics use <strong>median, P25/P75 and trimmed mean</strong>;
                            outliers are flagged and excluded from default averages. Historical
                            trends derive from our own daily snapshots.
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={captureSnapshot}
                        disabled={snapshotting}
                        data-testid="capture-snapshot-btn"
                    >
                        <Camera className="w-4 h-4 mr-2" />
                        {snapshotting ? "Capturing…" : "Capture snapshot now"}
                    </Button>
                </header>

                {/* KPI strip */}
                {loading || !overview ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="market-kpi-strip">
                        <Kpi label="Active listings" value={formatNumber(overview.total_active_listings)} sub={`${formatNumber(overview.verified_listings)} verified from source`} />
                        <Kpi
                            label="Valid for market stats"
                            value={formatNumber(overview.valid_for_market_stats)}
                            sub="Outliers excluded"
                            help="Listings that passed every data-quality check and contribute to medians/P25/P75."
                        />
                        <Kpi
                            label="Excluded outliers"
                            value={formatNumber(overview.excluded_outliers)}
                            sub="Tracked under Data Quality"
                            variant="warn"
                            help="Listings carrying any data-quality flag (price/m² outlier, missing fields, etc.) — never deleted, but kept out of default averages."
                            onClick={() => navigate("/quality")}
                        />
                        <Kpi label="Missing core fields" value={formatNumber((overview.missing_price || 0) + (overview.missing_m2 || 0) + (overview.missing_location || 0))} variant="warn" sub={`${overview.missing_price} no price · ${overview.missing_m2} no m² · ${overview.missing_location} no muni`} onClick={() => navigate("/quality")} />
                        <Kpi
                            label="Median €/m² · coastal"
                            value={overview.median_price_per_sqm_coastal != null ? `€${formatNumber(overview.median_price_per_sqm_coastal, 0)}` : "—"}
                            sub="Across municipalities with real data"
                            help="Pooled median €/m² across all coastal municipalities, computed only from outlier-filtered residential + commercial listings."
                        />
                        <Kpi
                            label="Median €/m² · preferred"
                            value={overview.median_price_per_sqm_preferred != null ? `€${formatNumber(overview.median_price_per_sqm_preferred, 0)}` : "—"}
                            sub="Porto MNE · Tivat · Budva · Kotor …"
                            help="Same calculation, but restricted to listings in the preferred coastal zones."
                        />
                        <Kpi label="Geocoded listings" value={`${formatNumber(geocodedListings.length)} / ${formatNumber(overview.total_active_listings)}`} sub="Coordinates resolved via Nominatim" />
                        <Kpi label="Historical days captured" value={formatNumber(history?.total_captured_days || 0)} sub={history?.available ? "Trend available" : `${history?.required_distinct_days || 2} needed — building history`} />
                    </div>
                )}

                {/* Charts row 1 */}
                <div className="grid lg:grid-cols-2 gap-6">
                    <Panel
                        title="Median €/m² by area"
                        subtitle="Median line + P25/P75 bars (outlier-filtered) · click an area to drill in"
                        icon={BarChart3}
                        data-testid="area-comparison-panel"
                    >
                        {areaComparison.length === 0 ? (
                            <EmptyState>Insufficient verified data for this chart.</EmptyState>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <ComposedChart data={areaComparison} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                    <XAxis
                                        dataKey="area"
                                        stroke="hsl(var(--muted-foreground))"
                                        tick={{ fontSize: 11, cursor: "pointer" }}
                                        onClick={(o) => {
                                            const a = areas.find((x) => x.area === o.value);
                                            if (a) setDrawerArea(a);
                                        }}
                                    />
                                    <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                                    <ChartTooltip
                                        content={<AreaTooltip />}
                                        cursor={{ fill: "rgba(64,180,180,0.06)" }}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Bar dataKey="p75" fill="rgba(64,180,180,0.20)" name="P75" onClick={(d) => { const a = areas.find((x) => x.area === d?.area); if (a) setDrawerArea(a); }} />
                                    <Bar dataKey="p25" fill="rgba(64,180,180,0.10)" name="P25" />
                                    <Line type="monotone" dataKey="median" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 4 }} name="Median" />
                                    <Line type="monotone" dataKey="trimmedMean" stroke="#f59e0b" strokeDasharray="4 3" strokeWidth={1.5} dot={false} name="Trimmed mean" />
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                    </Panel>

                    <Panel
                        title="Discount to area median"
                        subtitle="Click a bucket to open Deal Finder filtered on that range"
                        icon={Activity}
                        data-testid="discount-histogram-panel"
                    >
                        {discountHistogram.every((b) => b.count === 0) ? (
                            <EmptyState>Insufficient verified data for this chart.</EmptyState>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    data={discountHistogram}
                                    margin={{ top: 10, right: 20, left: 0, bottom: 30 }}
                                    onClick={(o) => {
                                        const label = o?.activeLabel;
                                        if (!label) return;
                                        navigate(`/deals?discountBucket=${encodeURIComponent(label)}`);
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                    <XAxis dataKey="bucket" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} />
                                    <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                                    <ChartTooltip content={<DiscountTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                                    <Bar dataKey="count" name="Listings" cursor="pointer">
                                        {discountHistogram.map((b) => <Cell key={b.bucket} fill={b.color} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Panel>
                </div>

                {/* Map */}
                <Panel
                    title="Market map"
                    subtitle="Real geocoded listings · color = flip score · zoom + click for details"
                    icon={MapIcon}
                    data-testid="market-map-panel"
                >
                    {geocodedListings.length === 0 ? (
                        <EmptyState>
                            No geocoded listings available yet. Run{" "}
                            <button onClick={() => navigate("/sources")} className="text-accent underline">
                                Sources → geocode pass
                            </button>{" "}
                            to populate coordinates.
                        </EmptyState>
                    ) : (
                        <div className="relative h-[480px] rounded-lg overflow-hidden border border-border/40" data-testid="market-map">
                            <MapContainer
                                center={[42.4304, 18.7710]}
                                zoom={9}
                                style={{ height: "100%", width: "100%" }}
                                scrollWheelZoom
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                {geocodedListings.map((l) => (
                                    <CircleMarker
                                        key={l.id}
                                        center={[l.latitude, l.longitude]}
                                        radius={Math.min(4 + (l.flip_score || 0) / 10, 14)}
                                        pathOptions={{
                                            color: ZONE_FILL[l.zone_class] || "#a3a3a3",
                                            fillColor: ZONE_FILL[l.zone_class] || "#a3a3a3",
                                            fillOpacity: l.excluded_from_market_stats ? 0.25 : 0.7,
                                            weight: 1,
                                        }}
                                        eventHandlers={{
                                            click: () => navigate(`/deals/${l.id}`),
                                        }}
                                    >
                                        <Popup>
                                            <div className="text-[12px] space-y-0.5">
                                                <div className="font-medium line-clamp-2 max-w-[220px]">{l.title}</div>
                                                <div>{[l.municipality, l.neighbourhood].filter(Boolean).join(" · ")}</div>
                                                <div>
                                                    {l.asking_price_eur ? formatEur(l.asking_price_eur) : "—"} ·{" "}
                                                    {l.price_per_sqm_eur ? `€${formatNumber(l.price_per_sqm_eur, 0)}/m²` : "no €/m²"}
                                                </div>
                                                <div>Flip score: {l.flip_score ?? "—"} · zone: {l.zone_class || "—"}</div>
                                            </div>
                                        </Popup>
                                    </CircleMarker>
                                ))}
                            </MapContainer>
                            <div className="absolute bottom-3 left-3 bg-background/90 border border-border/60 rounded-md px-3 py-2 text-[11px] flex flex-wrap gap-3 items-center pointer-events-none z-[400]">
                                {Object.entries(ZONE_FILL).map(([k, c]) => (
                                    <span key={k} className="flex items-center gap-1.5">
                                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />
                                        {k}
                                    </span>
                                ))}
                                <span className="text-muted-foreground">size = flip score · faded = excluded outlier</span>
                            </div>
                        </div>
                    )}
                </Panel>

                {/* Ranking lists */}
                <RankingsGrid rankings={rankings} loading={loading} navigate={navigate} />

                {/* Supply trend */}
                <Panel
                    title="Listing supply trend"
                    subtitle="From our proprietary daily snapshots — never backfilled"
                    icon={LineChartIcon}
                    data-testid="supply-trend-panel"
                >
                    {!history?.available ? (
                        <div className="flex flex-col items-center justify-center py-10 text-center">
                            <AlertTriangle className="w-6 h-6 text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground">{history?.reason}</p>
                            <div className="mt-3 text-[11px] text-muted-foreground space-y-0.5">
                                <div>Current snapshots: <span className="text-foreground">{history?.total_captured_days || 0}</span></div>
                                <div>Required: <span className="text-foreground">{history?.required_distinct_days || 2}</span></div>
                                {history?.first_snapshot_day && <div>First snapshot: <span className="text-foreground">{history.first_snapshot_day}</span></div>}
                                {history?.next_snapshot_scheduled_at && <div>Next scheduled: <span className="text-foreground">{history.next_snapshot_scheduled_at.slice(0, 16).replace("T", " ")} UTC</span></div>}
                            </div>
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={supplyTrend} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                                <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                                <ChartTooltip contentStyle={tooltipStyle} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Line type="monotone" dataKey="active" stroke="hsl(var(--accent))" strokeWidth={2} name="Active listings" />
                                <Line type="monotone" dataKey="outliers" stroke="#f59e0b" strokeWidth={2} name="Excluded outliers" />
                                <Line type="monotone" dataKey="reductions" stroke="#10b981" strokeWidth={2} name="Price reductions" />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </Panel>

                {/* Per-area intelligence */}
                <Panel
                    title="Per-area intelligence"
                    subtitle="Counts, robust price stats, sea-view share, scores · click a row for the area drawer"
                    icon={BarChart3}
                    data-testid="area-rollup-panel"
                >
                    {areas.length === 0 ? (
                        <EmptyState>Insufficient verified data.</EmptyState>
                    ) : (
                        <div className="overflow-x-auto -mx-6 px-6">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-card z-10">
                                    <tr className="border-b border-border/60 text-xs uppercase tracking-wider text-muted-foreground">
                                        <Th>Area</Th>
                                        <Th right>Active</Th>
                                        <Th right help="Listings carrying any data-quality flag — excluded from medians.">Outliers</Th>
                                        <Th right help="Median €/m² is the middle value of all valid listings. Robust to extremes.">Median €/m²</Th>
                                        <Th right help="25th percentile — 25% of listings cost less than this per m².">P25</Th>
                                        <Th right help="75th percentile — 25% of listings cost more than this per m².">P75</Th>
                                        <Th right help="Average after dropping the top and bottom 10% of values. Less sensitive to outliers than the raw mean.">Trimmed mean</Th>
                                        <Th right>Median size</Th>
                                        <Th right help="Share of listings whose sea-view status is confirmed yes/partial.">Sea view %</Th>
                                        <Th right help="Score 0-100 derived from new-listing flow vs. removed flow over the last 30 days.">Liquidity</Th>
                                        <Th right help="Composite score 0-100: % of listings priced below area median (60% weight) + sea-view share (40% weight).">Flip opp.</Th>
                                        <Th right help="Share of listings with NO data-quality flags — a higher number means the area's data is cleaner.">DQ score</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {areas.map((a) => (
                                        <tr
                                            key={a.area}
                                            className="border-b border-border/30 hover:bg-muted/30 transition-colors cursor-pointer"
                                            onClick={() => setDrawerArea(a)}
                                            data-testid={`area-row-${a.area}`}
                                        >
                                            <Td><span className="font-medium">{a.area}</span></Td>
                                            <Td right>{formatNumber(a.active_listings)}</Td>
                                            <Td right>
                                                {a.excluded_outliers > 0 ? (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); navigate("/quality"); }}
                                                        className="text-amber-400 hover:underline"
                                                    >
                                                        {formatNumber(a.excluded_outliers)}
                                                    </button>
                                                ) : "—"}
                                            </Td>
                                            <Td right>{a.median_price_per_sqm != null ? `€${formatNumber(a.median_price_per_sqm, 0)}` : "—"}</Td>
                                            <Td right>{a.p25_price_per_sqm != null ? `€${formatNumber(a.p25_price_per_sqm, 0)}` : "—"}</Td>
                                            <Td right>{a.p75_price_per_sqm != null ? `€${formatNumber(a.p75_price_per_sqm, 0)}` : "—"}</Td>
                                            <Td right>{a.trimmed_mean_price_per_sqm != null ? `€${formatNumber(a.trimmed_mean_price_per_sqm, 0)}` : "—"}</Td>
                                            <Td right>{a.median_size_sqm != null ? `${formatNumber(a.median_size_sqm, 0)} m²` : "—"}</Td>
                                            <Td right>{a.sea_view_confirmed_pct > 0 ? formatPct(a.sea_view_confirmed_pct, 0) : <span className="text-muted-foreground">unknown</span>}</Td>
                                            <Td right>{formatNumber(a.liquidity_score, 0)}</Td>
                                            <Td right>{a.flip_opportunity_score}</Td>
                                            <Td right>
                                                <span className={a.data_quality_score >= 90 ? "text-emerald-400" : a.data_quality_score >= 75 ? "text-amber-400" : "text-destructive"}>
                                                    {a.data_quality_score}%
                                                </span>
                                            </Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Panel>

                <Card className="p-4 border-dashed">
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                        <strong className="text-foreground">Sources &amp; methodology.</strong> Market statistics are based on scraped asking prices from
                        {" "}{Object.keys(overview?.listings_per_source || {}).join(", ") || "real-estate portals"}, not transaction prices.
                        Outliers (PRICE_OUTLIER_*, PRICE_PER_M2_OUTLIER_*, POSSIBLE_RENTAL_NOT_SALE, MISSING_*, LIVING_AREA_OUTLIER_*)
                        are excluded from default statistics. Official / public datasets (MONSTAT new-build apartment prices, Central Bank Montenegro
                        real-estate reports) will be shown separately on a future "Macro &amp; Historical" page once their connectors are built.
                    </p>
                </Card>

                <AreaDetailDrawer
                    area={drawerArea}
                    allListings={allListings}
                    onClose={() => setDrawerArea(null)}
                    onNavigate={navigate}
                />
            </div>
        </TooltipProvider>
    );
};

// ── chart tooltips ──────────────────────────────────────────────────────
const tooltipStyle = {
    background: "rgba(11, 18, 22, 0.95)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 6,
    fontSize: 12,
};

const AreaTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
        <div className="bg-popover border border-border/60 rounded-md p-3 text-xs space-y-1 shadow-lg">
            <div className="font-medium text-foreground">{d.area}</div>
            <div>Median: <span className="text-accent">€{formatNumber(d.median, 0)}/m²</span></div>
            <div>P25–P75: €{formatNumber(d.p25, 0)} – €{formatNumber(d.p75, 0)}</div>
            <div>Trimmed mean: €{formatNumber(d.trimmedMean, 0)}</div>
            <div className="text-muted-foreground">Valid listings: {d.listings} · Outliers: {d.outliers}</div>
        </div>
    );
};

const DiscountTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
        <div className="bg-popover border border-border/60 rounded-md p-3 text-xs space-y-0.5 shadow-lg">
            <div className="font-medium">{d.bucket}</div>
            <div>{d.count} listings ({formatNumber(d.pct, 1)}% of valid)</div>
            <div className="text-muted-foreground text-[10px] mt-1">Click bar to open in Deal Finder</div>
        </div>
    );
};

// ── ranking grid ────────────────────────────────────────────────────────
const RANKING_META = [
    { key: "top_flip_score", label: "Top Flip Score", icon: Flame, hint: "Highest residential flip score · outlier-free" },
    { key: "lowest_ppm", label: "Lowest €/m²", icon: Coins, hint: "Cheapest €/m² (residential, outlier-filtered)" },
    { key: "under_market", label: "Under Market", icon: TrendingDown, hint: "≥5% below same-category area median" },
    { key: "price_drops", label: "Price Drops", icon: TrendingDown, hint: "Listings with status=price_reduced" },
    { key: "preferred_zone_deals", label: "Preferred Zone Deals", icon: Star, hint: "Tivat · Budva · Kotor · Porto MNE · etc. with flip ≥ 50" },
    { key: "needs_review", label: "Needs Review", icon: AlertCircle, hint: "Carries one or more data-quality flags" },
    { key: "highest_data_confidence", label: "Highest Data Confidence", icon: ShieldCheck, hint: "Provenance confidence ≥ 0.7" },
    { key: "sea_view_potential", label: "Best Sea-View Potential", icon: Waves, hint: "sea_view ∈ {yes, partial, likely}" },
];

const RankingsGrid = ({ rankings, loading, navigate }) => {
    if (loading || !rankings) {
        return (
            <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-72" />)}
            </div>
        );
    }
    return (
        <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="rankings-grid">
            {RANKING_META.map((m) => (
                <RankingCard
                    key={m.key}
                    meta={m}
                    items={rankings[m.key] || []}
                    navigate={navigate}
                />
            ))}
        </div>
    );
};

const RankingCard = ({ meta, items, navigate }) => {
    const Icon = meta.icon;
    return (
        <Card className="p-4 flex flex-col" data-testid={`ranking-${meta.key}`}>
            <div className="flex items-start justify-between mb-3">
                <div>
                    <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-accent" />
                        <h3 className="text-sm font-medium">{meta.label}</h3>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{meta.hint}</p>
                </div>
                <Badge variant="outline" className="text-[10px]">{items.length}</Badge>
            </div>
            {items.length < 3 ? (
                <div className="flex-1 flex items-center justify-center text-center py-8">
                    <p className="text-[11px] text-muted-foreground italic">Insufficient verified data for this ranking.</p>
                </div>
            ) : (
                <ul className="space-y-1.5 flex-1 overflow-hidden">
                    {items.slice(0, 8).map((it, i) => (
                        <li key={it.id}>
                            <button
                                onClick={() => navigate(`/deals/${it.id}`)}
                                className="w-full text-left p-2 rounded-md hover:bg-muted/40 transition-colors group"
                                data-testid={`ranking-item-${meta.key}-${i}`}
                            >
                                <div className="flex items-start gap-2">
                                    <span className="text-[10px] text-muted-foreground mt-0.5 font-mono">#{i + 1}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[12px] line-clamp-1 group-hover:text-accent">{it.title || "Untitled"}</div>
                                        <div className="text-[10px] text-muted-foreground flex flex-wrap gap-x-2">
                                            <span>{[it.municipality, it.neighbourhood].filter(Boolean).join(" · ") || "—"}</span>
                                            {it.price_per_sqm_eur && <span>€{formatNumber(it.price_per_sqm_eur, 0)}/m²</span>}
                                            {it.flip_score != null && <span>flip {Math.round(it.flip_score)}</span>}
                                            {it.discount_to_median_pct != null && (
                                                <span className={it.discount_to_median_pct > 0 ? "text-emerald-400" : "text-rose-400"}>
                                                    {it.discount_to_median_pct > 0 ? "−" : "+"}
                                                    {Math.abs(it.discount_to_median_pct).toFixed(0)}%
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
};

// ── area detail drawer ──────────────────────────────────────────────────
const AreaDetailDrawer = ({ area, allListings, onClose, onNavigate }) => {
    const open = !!area;
    const data = useMemo(() => {
        if (!area) return null;
        const items = allListings.filter((l) => l.municipality === area.area);
        const valid = items.filter((l) => !l.excluded_from_market_stats);
        const top = [...valid].sort((a, b) => (b.flip_score || 0) - (a.flip_score || 0)).slice(0, 5);
        const underMarket = [...valid]
            .filter((l) => l.price_per_sqm_eur && area.median_price_per_sqm)
            .map((l) => ({
                ...l,
                _disc: ((area.median_price_per_sqm - l.price_per_sqm_eur) / area.median_price_per_sqm) * 100,
            }))
            .filter((l) => l._disc >= 5)
            .sort((a, b) => b._disc - a._disc)
            .slice(0, 5);
        const drops = items.filter((l) => l.status === "price_reduced").slice(0, 5);
        const geocoded = items.filter((l) => l.latitude && l.longitude).length;
        return { items, valid, top, underMarket, drops, geocoded };
    }, [area, allListings]);

    if (!area || !data) return null;

    return (
        <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
            <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="area-drawer">
                <SheetHeader>
                    <SheetTitle className="font-serif text-2xl">{area.area}</SheetTitle>
                    <p className="text-xs text-muted-foreground">
                        {area.active_listings} valid · {area.excluded_outliers} excluded · {data.geocoded} geocoded
                    </p>
                </SheetHeader>

                <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
                    <DrawerStat label="Median €/m²" value={area.median_price_per_sqm ? `€${formatNumber(area.median_price_per_sqm, 0)}` : "—"} />
                    <DrawerStat label="Trimmed mean" value={area.trimmed_mean_price_per_sqm ? `€${formatNumber(area.trimmed_mean_price_per_sqm, 0)}` : "—"} />
                    <DrawerStat label="P25 €/m²" value={area.p25_price_per_sqm ? `€${formatNumber(area.p25_price_per_sqm, 0)}` : "—"} />
                    <DrawerStat label="P75 €/m²" value={area.p75_price_per_sqm ? `€${formatNumber(area.p75_price_per_sqm, 0)}` : "—"} />
                    <DrawerStat label="Median size" value={area.median_size_sqm ? `${formatNumber(area.median_size_sqm, 0)} m²` : "—"} />
                    <DrawerStat label="Avg bedrooms" value={area.avg_bedrooms ?? "—"} />
                    <DrawerStat label="Sea-view %" value={area.sea_view_confirmed_pct > 0 ? formatPct(area.sea_view_confirmed_pct, 0) : "unknown"} />
                    <DrawerStat label="Liquidity score" value={formatNumber(area.liquidity_score, 0)} />
                    <DrawerStat label="Flip opp." value={area.flip_opportunity_score} />
                    <DrawerStat label="DQ score" value={`${area.data_quality_score}%`} variant={area.data_quality_score >= 90 ? "ok" : area.data_quality_score >= 75 ? "warn" : "bad"} />
                    <DrawerStat label="Price reductions" value={area.price_reductions} />
                    <DrawerStat label="Avg reduction %" value={area.avg_price_reduction_pct != null ? `${area.avg_price_reduction_pct}%` : "—"} />
                </div>

                <DrawerList title="Top 5 by flip score" items={data.top} onClick={(id) => onNavigate(`/deals/${id}`)} testid="drawer-top" />
                {data.underMarket.length > 0 && (
                    <DrawerList
                        title={`${data.underMarket.length} under-market listings`}
                        items={data.underMarket}
                        onClick={(id) => onNavigate(`/deals/${id}`)}
                        testid="drawer-undermarket"
                    />
                )}
                {data.drops.length > 0 && (
                    <DrawerList
                        title={`${data.drops.length} price drops`}
                        items={data.drops}
                        onClick={(id) => onNavigate(`/deals/${id}`)}
                        testid="drawer-drops"
                    />
                )}

                <div className="mt-6 pt-4 border-t border-border/40 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => onNavigate(`/deals?municipality=${encodeURIComponent(area.area)}`)} data-testid="drawer-open-in-deals">
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open in Deal Finder
                    </Button>
                    {area.excluded_outliers > 0 && (
                        <Button size="sm" variant="outline" onClick={() => onNavigate("/quality")}>
                            Review {area.excluded_outliers} outliers
                        </Button>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

const DrawerStat = ({ label, value, variant }) => (
    <div className={`p-3 rounded-md border ${variant === "ok" ? "border-emerald-500/30" : variant === "warn" ? "border-amber-500/30" : variant === "bad" ? "border-destructive/30" : "border-border/40"}`}>
        <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="text-foreground mt-0.5">{value}</div>
    </div>
);

const DrawerList = ({ title, items, onClick, testid }) => (
    <div className="mt-5">
        <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{title}</h4>
        <div className="space-y-1.5" data-testid={testid}>
            {items.map((l) => (
                <button
                    key={l.id}
                    onClick={() => onClick(l.id)}
                    className="w-full text-left p-2 rounded-md hover:bg-muted/40 transition-colors"
                >
                    <div className="text-[12px] line-clamp-1">{l.title || "Untitled"}</div>
                    <div className="text-[10px] text-muted-foreground flex flex-wrap gap-x-2">
                        {l.asking_price_eur && <span>{formatEur(l.asking_price_eur)}</span>}
                        {l.price_per_sqm_eur && <span>€{formatNumber(l.price_per_sqm_eur, 0)}/m²</span>}
                        {l.flip_score != null && <span>flip {Math.round(l.flip_score)}</span>}
                        {l._disc != null && <span className="text-emerald-400">−{l._disc.toFixed(0)}%</span>}
                    </div>
                </button>
            ))}
        </div>
    </div>
);

// ── primitives ──────────────────────────────────────────────────────────
const Kpi = ({ label, value, sub, variant, help, onClick }) => {
    const cn = `p-4 ${onClick ? "cursor-pointer hover:border-accent/40" : ""} ${variant === "warn" ? "border-amber-500/30" : ""}`;
    return (
        <Card className={cn} onClick={onClick} data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                {label}
                {help && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <HelpCircle className="w-3 h-3 text-muted-foreground/60 hover:text-foreground" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-[11px]">{help}</TooltipContent>
                    </Tooltip>
                )}
            </div>
            <div className="text-2xl font-serif mt-2">{value}</div>
            {sub && <div className="text-[11px] text-muted-foreground mt-1 leading-tight">{sub}</div>}
        </Card>
    );
};

const Panel = ({ title, subtitle, icon: Icon, children, ...rest }) => (
    <Card className="p-6" {...rest}>
        <div className="flex items-start gap-3 mb-4">
            {Icon && <Icon className="w-4 h-4 text-accent mt-1" />}
            <div>
                <h2 className="text-lg font-serif">{title}</h2>
                {subtitle && <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
        </div>
        {children}
    </Card>
);

const EmptyState = ({ children }) => (
    <div className="flex flex-col items-center justify-center py-10 text-center">
        <AlertTriangle className="w-6 h-6 text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">{children}</p>
    </div>
);

const Th = ({ children, right, help }) => (
    <th className={`py-2 px-2 font-medium ${right ? "text-right" : "text-left"}`}>
        <span className="inline-flex items-center gap-1">
            {children}
            {help && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-muted-foreground/60 hover:text-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-[11px]">{help}</TooltipContent>
                </Tooltip>
            )}
        </span>
    </th>
);
const Td = ({ children, right }) => (
    <td className={`py-2 px-2 ${right ? "text-right" : ""}`}>{children}</td>
);

export default Market;
